import React, { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote,
  Minus, Undo2, Redo2, RemoveFormatting,
} from "lucide-react";
import MediaPicker from "./MediaPicker";

/**
 * TipTap rich-text editor. Saves sanitized HTML to onChange(html).
 * `value` is HTML string. `placeholder` shown when empty.
 */
export default function RichText({ value = "", onChange, placeholder = "Start writing…", testId = "richtext" }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      // StarterKit v3 ships Link + Underline; disable them so our configured ones win.
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { loading: "lazy" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-navy max-w-none min-h-[180px] focus:outline-none px-4 py-3 text-navy-800",
        "data-testid": `${testId}-area`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value -> editor (e.g. when switching record).
  useEffect(() => {
    if (!editor) return;
    if ((value || "") !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertImage = (item) => {
    if (!editor || !item?.url) return;
    const fullUrl = item.url.startsWith("http")
      ? item.url
      : `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
    editor.chain().focus().setImage({ src: fullUrl, alt: item.alt || "" }).run();
    setPickerOpen(false);
  };

  if (!editor) return null;

  const btn = (active, onClick, Icon, label, tid) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
        active ? "bg-navy text-white" : "text-navy-700 hover:bg-navy-50"
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="rounded-2xl border border-navy-100 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-2 border-b border-navy-100 bg-soft-gray">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold", `${testId}-bold`)}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic", `${testId}-italic`)}
        {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, "Underline", `${testId}-underline`)}
        <span className="mx-1 h-5 w-px bg-navy-100" />
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading 2", `${testId}-h2`)}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Heading 3", `${testId}-h3`)}
        <span className="mx-1 h-5 w-px bg-navy-100" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bullet list", `${testId}-bullet`)}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list", `${testId}-ordered`)}
        <span className="mx-1 h-5 w-px bg-navy-100" />
        {btn(editor.isActive("link"), setLink, LinkIcon, "Link", `${testId}-link`)}
        {btn(false, () => setPickerOpen(true), ImageIcon, "Insert image", `${testId}-image`)}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Blockquote", `${testId}-quote`)}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Horizontal rule", `${testId}-hr`)}
        <span className="mx-1 h-5 w-px bg-navy-100" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo", `${testId}-undo`)}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo", `${testId}-redo`)}
        {btn(false, () => editor.chain().focus().unsetAllMarks().clearNodes().run(), RemoveFormatting, "Clear formatting", `${testId}-clear`)}
      </div>
      <EditorContent editor={editor} />
      {pickerOpen && (
        <MediaPicker
          kind="image"
          onClose={() => setPickerOpen(false)}
          onSelect={insertImage}
        />
      )}
    </div>
  );
}
